import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign,
  FileText,
  Plus,
  Loader2,
  Download,
  X,
  Check,
  Send,
  RotateCcw,
  Trash2,
  Search,
  Link2,
  Mic,
} from "lucide-react";
import AdminVoiceFillModal from "@/components/AdminVoiceFillModal";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-clinical-bg text-clinical-subtext" },
  sent: { label: "Sent", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800" },
  void: { label: "Void", cls: "bg-destructive/10 text-destructive" },
};

function money(n) {
  return `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatTile({ label, value, sublabel, accent, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="mt-2 font-heading text-2xl font-semibold text-clinical-text">
            {value}
          </div>
          {sublabel ? (
            <div className="text-[11px] text-clinical-subtext mt-0.5">{sublabel}</div>
          ) : null}
        </div>
        <span
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${accent}1a`, color: accent }}
        >
          <DollarSign className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

export default function AdminBilling() {
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l, c] = await Promise.all([
        api.get("/admin/invoices/summary"),
        api.get("/admin/invoices"),
        api.get("/clients"),
      ]);
      setSummary(s.data);
      setInvoices(l.data || []);
      setClients(c.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (i.client_name || "").toLowerCase().includes(q) ||
        (i.number || "").toLowerCase().includes(q)
      );
    });
  }, [invoices, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { all: invoices.length, draft: 0, sent: 0, paid: 0, void: 0 };
    invoices.forEach((i) => {
      c[i.status] = (c[i.status] || 0) + 1;
    });
    return c;
  }, [invoices]);

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading billing…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Finance</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            Billing &amp; Invoices
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Generate invoices from completed visits, mark paid, export PDF.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowGenerate(true)}
          className="btn-primary"
          data-testid="generate-invoice-button"
        >
          <Plus className="h-4 w-4" /> Generate invoice
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Outstanding"
          value={money(summary.outstanding)}
          sublabel={`${summary.counts.sent} sent`}
          accent="#F59E0B"
          testid="billing-stat-outstanding"
        />
        <StatTile
          label="Paid (last 30d)"
          value={money(summary.paid)}
          sublabel={`${summary.counts.paid} total paid`}
          accent="#10B981"
          testid="billing-stat-paid"
        />
        <StatTile
          label="Drafts"
          value={money(summary.draft)}
          sublabel={`${summary.counts.draft} not yet sent`}
          accent="#004AE6"
          testid="billing-stat-draft"
        />
        <StatTile
          label="Total billed"
          value={money(summary.total_billed)}
          sublabel="All-time, excluding void"
          accent="#FF46A3"
          testid="billing-stat-total"
        />
      </div>

      <div className="card-surface p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="flex flex-wrap gap-1.5 bg-clinical-bg p-1 rounded-lg" data-testid="invoice-status-filter">
            {["all", "draft", "sent", "paid", "void"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                data-testid={`invoice-filter-${s}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors capitalize ${
                  statusFilter === s
                    ? "bg-white text-clinical-text shadow-sm"
                    : "text-clinical-subtext hover:text-clinical-text"
                }`}
              >
                {s === "all" ? "All" : s}
                <span className="ml-1.5 text-[10px] text-clinical-subtext">{counts[s] || 0}</span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 md:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-clinical-subtext" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search client or invoice #…"
              data-testid="invoice-search-input"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-clinical-border text-sm focus:outline-none focus:ring-2 focus:ring-clinical-primary/30"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-clinical-subtext" data-testid="invoices-empty">
            {invoices.length === 0
              ? "No invoices yet. Click 'Generate invoice' to bill a client for a period."
              : "No invoices match the current filter."}
          </div>
        ) : (
          <ul className="divide-y divide-clinical-border" data-testid="invoices-list">
            {filtered.map((inv) => {
              const m = STATUS_META[inv.status] || STATUS_META.draft;
              return (
                <li
                  key={inv.id}
                  className="py-3 flex items-center gap-3 flex-wrap"
                  data-testid={`invoice-row-${inv.id}`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-clinical-text">{inv.number}</span>
                      <span className="text-clinical-subtext">·</span>
                      <span className="text-sm text-clinical-text">{inv.client_name}</span>
                      <span className={`pill ${m.cls}`}>{m.label}</span>
                    </div>
                    <div className="text-[11px] text-clinical-subtext mt-0.5">
                      {inv.period_start} – {inv.period_end} · {inv.line_items?.length || 0} line items
                      {inv.due_date ? ` · due ${inv.due_date}` : ""}
                    </div>
                  </div>
                  <div className="font-heading text-base font-semibold text-clinical-text">
                    {money(inv.total)}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpenInvoice(inv)}
                    className="btn-secondary"
                    data-testid={`invoice-open-${inv.id}`}
                  >
                    Open
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showGenerate && (
        <GenerateInvoiceModal
          clients={clients}
          onClose={() => setShowGenerate(false)}
          onSaved={(inv) => {
            setShowGenerate(false);
            load();
            setOpenInvoice(inv);
          }}
        />
      )}
      {openInvoice && (
        <InvoiceDetailModal
          invoice={openInvoice}
          onClose={() => setOpenInvoice(null)}
          onChanged={(inv) => {
            setOpenInvoice(inv);
            load();
          }}
        />
      )}
    </div>
  );
}

function GenerateInvoiceModal({ clients, onClose, onSaved }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    client_id: "",
    period_start: firstOfMonth,
    period_end: today.toISOString().slice(0, 10),
    rate_override: "",
    tax_rate: "",
    due_in_days: 14,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const client = clients.find((c) => c.id === form.client_id);

  const save = async () => {
    if (!form.client_id) {
      toast.error("Pick a client");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        period_start: form.period_start,
        period_end: form.period_end,
        rate_override: form.rate_override === "" ? null : Number(form.rate_override),
        tax_rate: form.tax_rate === "" ? 0 : Number(form.tax_rate) / 100,
        due_in_days: Number(form.due_in_days || 14),
        notes: form.notes,
      };
      const { data } = await api.post("/admin/invoices/generate", payload);
      toast.success(`Invoice ${data.number} created`);
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="generate-invoice-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-lg font-medium">Generate invoice</h3>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost p-1.5"
            data-testid="generate-invoice-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-eyebrow mb-1.5 block">Client</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="input-clinical"
              data-testid="invoice-client-select"
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.billing_rate ? ` — $${c.billing_rate}/h` : " — no rate set"}
                </option>
              ))}
            </select>
            {client && !client.billing_rate && (
              <div className="text-[11px] text-amber-700 mt-1.5">
                No default rate on this client. Set one in the client profile, or override below.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Period start</label>
              <input
                type="date"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="input-clinical"
                data-testid="invoice-period-start"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Period end</label>
              <input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="input-clinical"
                data-testid="invoice-period-end"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Rate override</label>
              <input
                type="number"
                step="0.01"
                placeholder={client?.billing_rate ? String(client.billing_rate) : "—"}
                value={form.rate_override}
                onChange={(e) => setForm({ ...form, rate_override: e.target.value })}
                className="input-clinical"
                data-testid="invoice-rate-override"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Tax %</label>
              <input
                type="number"
                step="0.01"
                placeholder="0"
                value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                className="input-clinical"
                data-testid="invoice-tax-rate"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Due in (days)</label>
              <input
                type="number"
                value={form.due_in_days}
                onChange={(e) => setForm({ ...form, due_in_days: e.target.value })}
                className="input-clinical"
                data-testid="invoice-due-days"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-clinical-subtext">Notes (visible on PDF)</label>
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="inline-flex items-center gap-1 rounded-lg bg-purple-100 px-2 py-1 text-[11px] font-semibold text-purple-700 hover:bg-purple-200"
                data-testid="invoice-voice-memo-btn"
              >
                <Mic size={11} /> Voice memo
              </button>
            </div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="input-clinical resize-y"
              data-testid="invoice-notes-input"
              placeholder="e.g. Thank you for choosing Afyanex Care."
            />
          </div>

          <AdminVoiceFillModal
            open={voiceOpen}
            onClose={() => setVoiceOpen(false)}
            endpoint="/ai/voice-fill/invoice-memo"
            title="Voice invoice memo"
            hint='e.g. "Invoice Margaret for February. Add 4 hours of overnight care at $32 an hour and a $50 medication management fee. Send to the family."'
            examples={[
              "Invoice Margaret for February. Add 4 hours overnight at $32 an hour and a $50 medication management fee.",
              "Bill Henry for last week. 12 hours of personal care.",
              "Send Eleanor's January invoice to the daughter — same as last month.",
            ]}
            limitMs={90 * 1000}
            filenamePrefix="invmemo"
            testIdPrefix="invoice-voice"
            renderPreview={(r) => (
              <div className="space-y-2 text-sm">
                {r.client_name && (
                  <div>
                    <div className="label-eyebrow">Client</div>
                    <p className="text-clinical-text">
                      {r.client_name}
                      {r.matched_client_id && <span className="ml-1 text-green-700">✓ matched</span>}
                    </p>
                  </div>
                )}
                {r.memo && (
                  <div>
                    <div className="label-eyebrow">Memo</div>
                    <p className="text-clinical-text">{r.memo}</p>
                  </div>
                )}
                {(r.line_items || []).length > 0 && (
                  <div>
                    <div className="label-eyebrow">Line items</div>
                    <ul className="text-clinical-text text-xs space-y-0.5">
                      {r.line_items.map((li, i) => (
                        <li key={i}>
                          {li.description}
                          {li.hours != null && ` · ${li.hours}h`}
                          {li.rate != null && ` @ $${li.rate}`}
                          {li.amount != null && ` = $${li.amount}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(r.period_start || r.period_end) && (
                  <div className="text-xs text-clinical-subtext">
                    Period: {r.period_start || "?"} – {r.period_end || "?"}
                  </div>
                )}
              </div>
            )}
            onConfirm={(r) => {
              const updates = {};
              if (r.matched_client_id) updates.client_id = r.matched_client_id;
              if (r.period_start) updates.period_start = r.period_start;
              if (r.period_end) updates.period_end = r.period_end;
              const memoBlock = [
                r.memo || "",
                (r.line_items || []).map((li) => {
                  const parts = [li.description];
                  if (li.hours != null) parts.push(`${li.hours}h`);
                  if (li.rate != null) parts.push(`@$${li.rate}`);
                  if (li.amount != null) parts.push(`= $${li.amount}`);
                  return `• ${parts.join(" ")}`;
                }).join("\n"),
              ].filter(Boolean).join("\n");
              if (memoBlock) {
                updates.notes = form.notes ? `${form.notes}\n${memoBlock}` : memoBlock;
              }
              setForm({ ...form, ...updates });
              toast.success("Invoice fields filled from voice");
            }}
            confirmLabel="Apply"
          />

          <div className="rounded-lg border border-clinical-border bg-clinical-bg p-3 text-[11px] text-clinical-subtext">
            We pull all <strong>completed visits</strong> in the date range for this client.
            Hours come from actual clock-out durations when available, else from the visit's
            start/end times. Visits without billable hours are skipped.
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            data-testid="generate-invoice-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary"
            data-testid="generate-invoice-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceDetailModal({ invoice, onClose, onChanged }) {
  const [saving, setSaving] = useState(false);
  const m = STATUS_META[invoice.status] || STATUS_META.draft;

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/invoices/${invoice.id}`, { status });
      toast.success(`Marked ${status}`);
      onChanged(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/invoices/${invoice.id}`);
      toast.success("Invoice deleted");
      onClose();
      onChanged(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.number || invoice.id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Could not download PDF");
    }
  };

  const sharePayLink = async () => {
    try {
      const { data } = await api.post(`/admin/invoices/${invoice.id}/share-link`);
      const fullUrl = `${window.location.origin}/family/invoice/${data.token}`;
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Pay link copied — share it with the family");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create pay link");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="invoice-detail-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-lg font-medium truncate">{invoice.number}</h3>
              <span className={`pill ${m.cls}`}>{m.label}</span>
            </div>
            <div className="text-[11px] text-clinical-subtext truncate">
              {invoice.client_name} · {invoice.period_start} – {invoice.period_end}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost p-1.5"
            data-testid="invoice-detail-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {invoice.line_items?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-clinical-border text-clinical-subtext text-xs uppercase">
                  <tr>
                    <th className="text-left py-2 px-1">Date</th>
                    <th className="text-left py-2 px-1">Caregiver</th>
                    <th className="text-right py-2 px-1">Hours</th>
                    <th className="text-right py-2 px-1">Rate</th>
                    <th className="text-right py-2 px-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((li, i) => (
                    <tr key={`bline-${i}-${li.date || ""}-${li.caregiver_name || ""}`} className="border-b border-clinical-border/60" data-testid={`invoice-line-${i}`}>
                      <td className="py-1.5 px-1">{li.date}</td>
                      <td className="py-1.5 px-1">{li.caregiver_name}</td>
                      <td className="py-1.5 px-1 text-right">{Number(li.hours || 0).toFixed(2)}</td>
                      <td className="py-1.5 px-1 text-right">{money(li.rate)}</td>
                      <td className="py-1.5 px-1 text-right font-medium">{money(li.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="py-1.5 px-1 text-right text-clinical-subtext">Subtotal</td>
                    <td className="py-1.5 px-1 text-right">{money(invoice.subtotal)}</td>
                  </tr>
                  {invoice.tax_rate > 0 && (
                    <tr>
                      <td colSpan={4} className="py-1.5 px-1 text-right text-clinical-subtext">
                        Tax ({(invoice.tax_rate * 100).toFixed(1)}%)
                      </td>
                      <td className="py-1.5 px-1 text-right">{money(invoice.tax_amount)}</td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-clinical-text">
                    <td colSpan={4} className="py-2 px-1 text-right font-semibold">Total</td>
                    <td className="py-2 px-1 text-right font-heading text-base font-semibold">
                      {money(invoice.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-sm text-clinical-subtext italic">No line items.</div>
          )}

          {invoice.notes ? (
            <div className="rounded-lg border border-clinical-border bg-clinical-bg p-3 text-xs">
              <div className="label-eyebrow mb-1">Notes</div>
              <div>{invoice.notes}</div>
            </div>
          ) : null}

          <div className="text-[11px] text-clinical-subtext">
            Issued {invoice.issue_date}
            {invoice.due_date ? ` · Due ${invoice.due_date}` : ""}
            {invoice.sent_at ? ` · Sent ${invoice.sent_at.slice(0, 10)}` : ""}
            {invoice.paid_at ? ` · Paid ${invoice.paid_at.slice(0, 10)}` : ""}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-between gap-2 px-5 py-4 border-t border-clinical-border flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={downloadPdf}
              className="btn-secondary"
              data-testid="invoice-download-pdf"
            >
              <Download className="h-4 w-4" /> PDF
            </button>
            {invoice.status !== "paid" && invoice.status !== "void" && (
              <button
                type="button"
                onClick={sharePayLink}
                className="btn-secondary"
                data-testid="invoice-share-pay-link"
                title="Copy a public Pay link to send to the family"
              >
                <Link2 className="h-4 w-4" /> Copy pay link
              </button>
            )}
            {invoice.status === "draft" || invoice.status === "void" ? (
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="btn-ghost !text-destructive"
                data-testid="invoice-delete"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {invoice.status === "draft" && (
              <button
                type="button"
                onClick={() => setStatus("sent")}
                disabled={saving}
                className="btn-secondary"
                data-testid="invoice-mark-sent"
              >
                <Send className="h-4 w-4" /> Mark sent
              </button>
            )}
            {(invoice.status === "draft" || invoice.status === "sent") && (
              <button
                type="button"
                onClick={() => setStatus("paid")}
                disabled={saving}
                className="btn-primary"
                data-testid="invoice-mark-paid"
              >
                <Check className="h-4 w-4" /> Mark paid
              </button>
            )}
            {invoice.status === "paid" && (
              <button
                type="button"
                onClick={() => setStatus("sent")}
                disabled={saving}
                className="btn-secondary"
                data-testid="invoice-unmark-paid"
              >
                <RotateCcw className="h-4 w-4" /> Revert to sent
              </button>
            )}
            {invoice.status !== "void" && (
              <button
                type="button"
                onClick={() => setStatus("void")}
                disabled={saving}
                className="btn-ghost"
                data-testid="invoice-void"
              >
                Void
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
